import React from 'react';
import {
  Dialog,
  DialogPortal,
  DialogTitle,
  DialogDescription,
  InlineDialogOverlay,
  InlineDialogContent,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import {
  getFormattedProxyType,
  getProxyTypeLabel,
  type SecondLayerProxyType,
  type FirstLayerProxyTypeValue,
} from 'venn-utils';

interface ChangeProxyTypeWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  newProxyTypeValue: FirstLayerProxyTypeValue;
  currentSecondLayerType: SecondLayerProxyType;
  currentSecondLayerExtrapolate: boolean | undefined;
  container: HTMLElement;
}

export const ChangeProxyTypeWarningDialog = ({
  open,
  onOpenChange,
  onConfirm,
  newProxyTypeValue,
  currentSecondLayerType,
  currentSecondLayerExtrapolate,
  container,
}: ChangeProxyTypeWarningDialogProps) => {
  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  const formattedNewType = getProxyTypeLabel(newProxyTypeValue);
  const formattedSecondLayerType = getFormattedProxyType(currentSecondLayerType, currentSecondLayerExtrapolate);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogPortal container={container}>
        <InlineDialogOverlay onClick={handleCancel} />
        <InlineDialogContent className="w-[550px] [&]:max-w-[550px] h-[280px] p-0 flex flex-col gap-0">
          <div className="h-[75px] items-center px-[18px] py-[16px] shrink-0">
            <DialogTitle className="text-[32px] font-normal leading-none">Change proxy type?</DialogTitle>
          </div>
          <Separator className="bg-venn-grey-30" />
          <div className="flex-1 px-[18px] py-[16px]">
            <DialogDescription className="text-[16px] text-venn-black">
              Changing this proxy from Backfill to {formattedNewType} will remove your {formattedSecondLayerType} proxy.
            </DialogDescription>
          </div>
          <Separator className="bg-venn-grey-30" />
          <div className="flex flex-row justify-between items-center gap-[6px] px-[18px] h-[64px] shrink-0">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="text-[11px] font-bold uppercase leading-none tracking-[1px]"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              className="uppercase text-[11px] font-bold tracking-[1px] leading-none bg-venn-error hover:bg-venn-strong-error"
            >
              Confirm proxy type change
            </Button>
          </div>
        </InlineDialogContent>
      </DialogPortal>
    </Dialog>
  );
};
